import { dereference } from '@jdw/jst';
import { Locale, getText, getTextFromTranslatable } from '../i18n/translatable.js';
import Ajv, {} from 'ajv/dist/2020.js';
import { TextType, Operation } from './schema.types.js';
import { lookupMessage } from '../i18n/dictionary.js';
import { dictionaries } from '../i18n/index.js';
export const TLMD_SCHEMA_BASE_URI_PATH = '/tlmd/';
const TLMD_SCHEMA_BASE_URI = 'https://om-app.com' + TLMD_SCHEMA_BASE_URI_PATH;
export const schemaId = (schemaPath) => schemaPath.startsWith(TLMD_SCHEMA_BASE_URI) ? schemaPath : TLMD_SCHEMA_BASE_URI + schemaPath;
const TLMD_SCHEMA_TYPE = 'https://json-schema.org/draft/2020-12/schema';
/**
 * Extract a JSON schema from a TLMD schema
 * @param tlmdSchema
 * @param locale
 * @param dictionary
 */
export const getJSONSchema = (tlmdSchema, locale, dictionary) => {
    const jsonSchema = tlmdSchema.schema;
    jsonSchema.$schema = jsonSchema.$schema ?? TLMD_SCHEMA_TYPE;
    jsonSchema.$id = jsonSchema.$id ?? schemaId(tlmdSchema.id);
    jsonSchema.title = jsonSchema.title ?? getTitle(tlmdSchema, locale, dictionary);
    jsonSchema.description = jsonSchema.description ?? getDescription(tlmdSchema, locale, dictionary);
    if (tlmdSchema.properties) {
        const tlmdObjectSchema = tlmdSchema;
        jsonSchema.type = jsonSchema.type ?? 'object';
        jsonSchema.properties = jsonSchema.properties ? jsonSchema.properties : {};
        Object.keys(tlmdObjectSchema.properties).forEach((propertyName) => {
            const definition = tlmdObjectSchema.properties[propertyName];
            jsonSchema.properties[propertyName] = {
                $ref: schemaId(definition.ref)
            };
        });
        jsonSchema.required = jsonSchema.required ? jsonSchema.required : [];
        Object.keys(tlmdObjectSchema.properties)
            .filter((propertyName) => tlmdObjectSchema.properties[propertyName].required &&
            jsonSchema.required.indexOf(propertyName) == -1)
            .forEach((propertyName) => {
            jsonSchema.required.push(propertyName);
        });
    }
    else if (tlmdSchema.input) {
        const tlmdInputSchema = tlmdSchema;
        if (tlmdInputSchema.enum) {
            jsonSchema.enum = tlmdInputSchema.enum.map((option) => option.value ?? option);
        }
    }
    return jsonSchema;
};
const getDictionaryKey = (tlmdSchema, type) => {
    return `schema.${tlmdSchema.id}.${type}`;
};
const getSchemaText = (tlmdSchema, type, locale, dictionary) => {
    let text = undefined;
    if (tlmdSchema.texts &&
        tlmdSchema.texts !== 'dictionary' &&
        tlmdSchema.texts[type]) {
        try {
            text = getText(tlmdSchema.texts[type], locale, dictionary);
        }
        catch (error) { }
    }
    if (!text) {
        const dictionaryKey = getDictionaryKey(tlmdSchema, type);
        text = lookupMessage(dictionaryKey, dictionary, undefined);
    }
    return text;
};
export const getTexts = (tlmdPropertySchema, locale, dictionary, tlmdObjectSchema, property, enhance) => {
    const baseFormDictionaryKey = tlmdObjectSchema
        ? `form.${tlmdObjectSchema.id}${property ? '.properties.' + property : ''}.`
        : undefined;
    let titleText = baseFormDictionaryKey
        ? lookupMessage(`${baseFormDictionaryKey}${TextType.title}`, dictionary, undefined)
        : undefined;
    let descriptionText = baseFormDictionaryKey
        ? lookupMessage(`${baseFormDictionaryKey}${TextType.description}`, dictionary, undefined)
        : undefined;
    let abbrText = baseFormDictionaryKey
        ? lookupMessage(`${baseFormDictionaryKey}${TextType.abbr}`, dictionary, undefined)
        : undefined;
    let ctiText = baseFormDictionaryKey
        ? lookupMessage(`${baseFormDictionaryKey}${TextType.cti}`, dictionary, undefined)
        : undefined;
    //try in schema dictionaries next
    const baseSchemaDictionaryKey = tlmdObjectSchema
        ? `schema.${tlmdObjectSchema.id}${property ? '.properties.' + property : ''}.`
        : undefined;
    titleText =
        titleText ??
            (baseSchemaDictionaryKey
                ? lookupMessage(`${baseSchemaDictionaryKey}${TextType.title}`, dictionary, undefined)
                : undefined);
    descriptionText =
        descriptionText ??
            (baseSchemaDictionaryKey
                ? lookupMessage(`${baseSchemaDictionaryKey}${TextType.description}`, dictionary, undefined)
                : undefined);
    abbrText =
        abbrText ??
            (baseFormDictionaryKey
                ? lookupMessage(`${baseSchemaDictionaryKey}${TextType.abbr}`, dictionary, undefined)
                : undefined);
    ctiText =
        ctiText ??
            (baseSchemaDictionaryKey
                ? lookupMessage(`${baseSchemaDictionaryKey}${TextType.cti}`, dictionary, undefined)
                : undefined);
    const autoEnhance = enhance === true;
    //try in property dictionaries next
    titleText = titleText ?? getTitle(tlmdPropertySchema, locale, dictionary);
    // titleText = titleText
    //   ? titleText
    //   : getTitle(tlmdPropertySchema, locale, dictionary);
    descriptionText =
        descriptionText ??
            getDescription(tlmdPropertySchema, locale, dictionary, autoEnhance) ??
            (autoEnhance ? titleText : undefined);
    // descriptionText = descriptionText
    //   ? descriptionText
    //   : titleText
    //   ? titleText
    //   : getDescription(tlmdPropertySchema, locale, dictionary);
    abbrText = abbrText ?? getAbbreviation(tlmdPropertySchema, locale, dictionary) ?? titleText;
    // abbrText = abbrText
    //   ? abbrText
    //   : titleText
    //   ? titleText
    //   : getAbbreviation(tlmdPropertySchema, locale, dictionary);
    ctiText =
        ctiText ??
            getCallToInput(tlmdPropertySchema, locale, dictionary, autoEnhance) ??
            (autoEnhance ? descriptionText : undefined);
    // ctiText = ctiText
    //   ? ctiText
    //   : descriptionText
    //   ? descriptionText
    //   : getCallToInput(tlmdPropertySchema, locale, dictionary);
    return {
        [TextType.title]: titleText,
        [TextType.description]: descriptionText,
        [TextType.abbr]: abbrText,
        [TextType.cti]: ctiText
    };
};
const getTitle = (tlmdSchema, locale, dictionary) => {
    return getSchemaText(tlmdSchema, TextType.title, locale, dictionary) ?? tlmdSchema.id;
};
const getAbbreviation = (tlmdSchema, locale, dictionary) => {
    return (getSchemaText(tlmdSchema, TextType.abbr, locale, dictionary) ??
        getTitle(tlmdSchema, locale, dictionary));
};
const getDescription = (tlmdSchema, locale, dictionary, enhance = false) => {
    return (getSchemaText(tlmdSchema, TextType.description, locale, dictionary) ??
        (enhance ? getTitle(tlmdSchema, locale, dictionary) : undefined));
};
const getCallToInput = (tlmdSchema, locale, dictionary, enhance = false) => {
    return (getSchemaText(tlmdSchema, TextType.cti, locale, dictionary) ??
        (enhance ? getDescription(tlmdSchema, locale, dictionary) : undefined));
};
export const getEnumOption = (tlmdInputSchema, index, locale, dictionary, transform) => {
    let option = undefined;
    if (tlmdInputSchema && tlmdInputSchema.enum && tlmdInputSchema.enum[index]) {
        const enumOption = tlmdInputSchema.enum[index];
        const value = enumOption.value ?? enumOption;
        option = { value: value, label: '' };
        if (enumOption.label) {
            option.label = getTextFromTranslatable(enumOption.label, locale);
        }
        else if (enumOption.dictionaryKey) {
            const message = lookupMessage(enumOption.dictionaryKey, dictionary, undefined);
            option.label =
                lookupMessage(enumOption.dictionaryKey, dictionary, undefined) ??
                    (typeof value === 'object' ? JSON.stringify(value) : value.toString());
            console.log('option=', option);
        }
        else {
            option.label = typeof value === 'object' ? JSON.stringify(value) : value.toString();
        }
        if (transform) {
            option.label = transform(option.label);
        }
    }
    return option;
};
export const checkTLMDSchema = (schema, schemaStore) => {
    const tlmdSchema = extractTLMDSchema(schema, schemaStore);
    if (tlmdSchema.properties) {
        const tlmdObjectSchema = tlmdSchema;
        const operations = Object.values(Operation);
        const occurrencesMap = operations
            .map((operation) => Object.keys(tlmdObjectSchema.properties).filter((propertyName) => tlmdObjectSchema.properties[propertyName].usage[operation] !== undefined))
            .filter((properties, index) => {
            if (properties.length === 0) {
                console.log(`INFO  schema '${schema}' not used in operation '${operations[index]}'`);
            }
            return properties.length > 0;
        })
            .reduce((occurrences, properties) => {
            properties.forEach((property) => {
                occurrences[property] =
                    occurrences[property] === undefined ? 1 : occurrences[property] + 1;
            });
            return occurrences;
        }, {});
        const properties = Object.keys(occurrencesMap);
        if (properties.length > 0) {
            const operationCount = Math.max(...Object.values(occurrencesMap));
            if (operationCount > 0) {
                if (operationCount === Math.max(...Object.values(occurrencesMap))) {
                    return true;
                }
                else {
                    const unbalancedProperties = properties
                        .filter((property) => occurrencesMap[property] !== operationCount)
                        .join(', ');
                    console.log(`ERROR schema '${schema}' is not balanced (${operationCount} operations expected) at properties ${unbalancedProperties}`);
                }
            }
            else {
                console.log(`ERROR schema '${schema}' not used in any operation`);
            }
        }
        else {
            console.log(`ERROR schema '${schema}' does not have any property`);
        }
        return false;
    }
    else {
        return true;
    }
};
export const getTLMDSchema = (schema, schemaStore, ajv, operation) => {
    const tlmdSchema = extractTLMDSchema(schema, schemaStore, operation);
    if (tlmdSchema) {
        loadTLMDSchemaInToAjv(tlmdSchema, schemaStore, ajv);
    }
    return tlmdSchema;
};
export const getInlineJSONSchema = (schema, schemaStore, ajv) => {
    const parsedSchemaId = parseSchemaId(schema);
    if (!ajv.getSchema(schemaId(parsedSchemaId.schemaId))?.schema) {
        //const tlmdSchema = extractTLMDSchema(schema, schemaStore);
        const tlmdSchema = schemaStore[parsedSchemaId.schemaId];
        if (tlmdSchema) {
            return loadTLMDSchemaInToAjv(tlmdSchema, schemaStore, ajv);
        }
        else {
            return undefined;
        }
    }
    else {
        return getInlineSchema(parsedSchemaId.schemaId, schemaStore, ajv);
    }
};
export const getValidator = (schema, schemaStore, ajv) => {
    const parsedSchemaId = parseSchemaId(schema);
    let ajvValidator = ajv.getSchema(schemaId(parsedSchemaId.schemaId));
    if (!ajvValidator) {
        const tlmdSchema = schemaStore[parsedSchemaId.schemaId];
        if (tlmdSchema) {
            loadTLMDSchemaInToAjv(tlmdSchema, schemaStore, ajv);
            ajvValidator = ajv.getSchema(schemaId(parsedSchemaId.schemaId));
        }
    }
    return ajvValidator;
};
const extractTLMDSchema = (schema, schemaStore, operation) => {
    const { schemaId, tag } = parseSchemaId(schema);
    const tlmdSchema = schemaStore[schemaId];
    if (tlmdSchema) {
        if (!tag) {
            return tlmdSchema;
        }
        else {
            const { properties, ...schemaWithoutProperties } = tlmdSchema;
            const selectedProperties = selectProperties(properties, tag, operation);
            //check if we got properties
            if (Object.keys(selectedProperties).length > 0) {
                const tlmdObjectSchema = schemaWithoutProperties;
                tlmdObjectSchema.id = getSchemaId(schemaId, tag);
                tlmdObjectSchema.properties = selectedProperties;
                return tlmdObjectSchema;
            }
        }
    }
    return undefined;
};
const loadTLMDSchemaInToAjv = (tlmdSchema, schemaStore, ajv, unresolvedIds = []) => {
    const parsedSchemaId = parseSchemaId(tlmdSchema.id);
    //check if we already have a validator for this schema
    parsedSchemaId.schemaId;
    let ajvValidator = ajv.getSchema(schemaId(parsedSchemaId.schemaId));
    if (!ajvValidator) {
        // console.debug(
        //   `Schema ${tlmdSchema.id} (full=${schemaId(
        //     tlmdSchema.id,
        //   )}) NOT found in ajv. Adding...`,
        // );
        //not yet in ajv
        //construct json schema from tlmd object schema
        const originalSchemaId = tlmdSchema.id;
        tlmdSchema.id = parsedSchemaId.schemaId; //schema id now stripped of any tags
        const jsonSchema = getJSONSchema(tlmdSchema, Locale.en, dictionaries[Locale.en]);
        //add the json schema to Ajv
        ajv.addSchema(jsonSchema);
        //now check if all references are available in ajv too
        const inlineSchema = getInlineSchema(tlmdSchema.id, schemaStore, ajv, unresolvedIds);
        tlmdSchema.id = originalSchemaId;
        return inlineSchema;
    }
    else {
        // console.debug(
        //   `Schema ${tlmdSchema.id} (full=${schemaId(
        //     tlmdSchema.id,
        //   )}) already added to ajv.`,
        // );
    }
};
const getInlineSchema = (tlmdSchemaId, schemaStore, ajv, unresolvedIds = []) => {
    const inlineSchema = dereference(ajv.getSchema(schemaId(tlmdSchemaId))?.schema, ((refId) => {
        const referenceValidator = ajv.getSchema(schemaId(refId));
        if (referenceValidator) {
            // console.debug(
            //   `Schema ${tlmdSchema.id} (full=${schemaId(
            //     tlmdSchema.id,
            //   )}) references ${refId} (full=${schemaId(
            //     refId,
            //   )}) which was already added to ajv.`,
            // );
            return referenceValidator.schema;
        }
        else {
            //the referred schema is not yet present in ajv
            const unresolvedIdIndex = unresolvedIds.indexOf(refId);
            if (unresolvedIdIndex === -1) {
                //          const referredTlmdSchema = extractTLMDSchema(refId, schemaStore);
                const referredTlmdSchema = schemaStore[refId];
                if (referredTlmdSchema) {
                    loadTLMDSchemaInToAjv(referredTlmdSchema, schemaStore, ajv, unresolvedIds.concat(refId));
                    const loadedReferenceValidator = ajv.getSchema(schemaId(refId));
                    if (loadedReferenceValidator) {
                        // console.debug(
                        //   `Schema ${tlmdSchema.id} (full=${schemaId(
                        //     tlmdSchema.id,
                        //   )}) references ${refId} (full=${schemaId(
                        //     refId,
                        //   )}) which was previously not present in ajv.`,
                        // );
                        return loadedReferenceValidator.schema;
                    }
                    else {
                        // console.debug(
                        //   `Schema ${tlmdSchema.id} (full=${schemaId(
                        //     tlmdSchema.id,
                        //   )}) references ${refId} (full=${schemaId(
                        //     refId,
                        //   )}) but could not be added.`,
                        // );
                        console.error(`Unable to dereference TLMD Object Schema ${refId}`);
                    }
                }
                else {
                    // console.debug(
                    //   `Schema ${tlmdSchema.id} (full=${schemaId(
                    //     tlmdSchema.id,
                    //   )}) references ${refId} (full=${schemaId(
                    //     refId,
                    //   )}) but is unknown.`,
                    // );
                    console.error(`Unable to find TLMD Object Schema for reference key ${refId}`);
                }
            }
            else {
                // console.debug(
                //   `Schema ${tlmdSchema.id} (full=${schemaId(
                //     tlmdSchema.id,
                //   )}) references ${refId} (full=${schemaId(
                //     refId,
                //   )}) which is a circular reference at level ${unresolvedIdIndex}.`,
                // );
                console.error(`Circular reference detected: ${refId} was already referenced at level ${unresolvedIdIndex}`);
            }
        }
        return undefined;
    }));
    return inlineSchema;
};
const selectProperties = (objectSchemaProperties, tag, operation) => {
    return Object.entries(objectSchemaProperties)
        .map(([propertyName, property]) => {
        const { usage, ...definition } = property;
        const selectedUsageDefinition = selectProperty(property, tag);
        const { ref, ...usageDefinition } = selectedUsageDefinition
            ? selectedUsageDefinition
            : { ref: undefined };
        return [propertyName, Object.assign(definition, ref ? { ref } : {}, {
                usage: usageDefinition && (!operation || usageDefinition[operation] !== undefined)
                    ? usageDefinition
                    : undefined
            })];
    })
        .filter(([_propertyName, definition]) => definition.usage)
        .reduce((map, entry) => {
        map[entry[0]] = entry[1];
        return map;
    }, {});
};
const getSchemaId = (_schemaId, tag, full = false) => {
    const shortId = `${_schemaId}${tag ? `--${tag}` : ''}`;
    return full ? schemaId(shortId) : shortId;
};
const parseSchemaId = (_schemaId) => {
    const parts = (_schemaId.startsWith(TLMD_SCHEMA_BASE_URI)
        ? _schemaId.substring(TLMD_SCHEMA_BASE_URI.length)
        : _schemaId).split('--');
    const schemaId = parts.splice(0, 1)[0];
    const tag = parts.length > 0 ? parts[0] : undefined;
    return { schemaId, tag };
};
const selectProperty = (property, tag) => {
    if (property && property.usage) {
        const matchingUsageDefinitions = property.usage.filter((usageDefinition) => (typeof usageDefinition === 'string' && usageDefinition === tag) ||
            usageDefinition.tag === tag);
        if (matchingUsageDefinitions.length === 1) {
            const match = matchingUsageDefinitions[0];
            if (typeof match === 'string') {
                return {
                    [Operation.CREATE]: 0,
                    [Operation.READ]: 0,
                    [Operation.UPDATE]: 0
                };
            }
            else {
                const typedMatch = match;
                if (typeof typedMatch.input === 'undefined') {
                    return {
                        ref: typedMatch.ref,
                        [Operation.CREATE]: 1,
                        [Operation.READ]: 1,
                        [Operation.UPDATE]: 1
                    };
                }
                else {
                    if (typeof typedMatch.input === 'number') {
                        return {
                            ref: typedMatch.ref,
                            [Operation.CREATE]: typedMatch.input,
                            [Operation.READ]: typedMatch.input,
                            [Operation.UPDATE]: typedMatch.input
                        };
                    }
                    else {
                        return Object.assign({ ref: typedMatch.ref }, typedMatch.input);
                    }
                }
            }
        }
        else {
            if (matchingUsageDefinitions.length > 1) {
                console.error(`Tag '${tag}' yields more than one usages in property ${JSON.stringify(property, undefined, 2)}`);
            }
        }
    }
    return undefined;
};
